import { PulseCategory, PulseItem } from "@/models/pulse";

export const CATEGORY_META: Record<PulseCategory, { label: string; tag: string; href: string }> = {
    shorts: { label: "Short interest", tag: "SHORTS", href: "/short-interest" },
    insiders: { label: "Insiders", tag: "INSIDER", href: "/insiders" },
    gov: { label: "Gov trades", tag: "GOV", href: "/us-gov-trades" },
    options: { label: "Options flow", tag: "OPTIONS", href: "/options" },
    alt: { label: "Alt data", tag: "ALT DATA", href: "/alt-data" },
    signals: { label: "Signals", tag: "SIGNAL", href: "/market-intelligence" },
    macro: { label: "Macro", tag: "MACRO", href: "/currencies" },
    news: { label: "News", tag: "NEWS", href: "/news" },
};

export const stockHref = (exchange?: string | null, ticker?: string | null) => (exchange && ticker ? `/s/${exchange}/${ticker}` : null);

export const itemHref = (item: PulseItem) => stockHref(item.exchangeSymbol, item.ticker) || item.link || CATEGORY_META[item.category]?.href || "/";

export const timeAgo = (iso: string | null, now: number = Date.now()) => {
    if (!iso) return "";
    const minutes = Math.round((now - new Date(iso).getTime()) / 60000);
    if (minutes < 1) return "now";
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
};

export const clockTime = (iso: string | null) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
};

export const money = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "—";
    const abs = Math.abs(value);
    if (abs >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (abs >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
};

export const signed = (value: number | null | undefined, digits = 2, suffix = "") => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    return `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(digits)}${suffix}`;
};

export const pct = (value: number | null | undefined, digits = 1) => (value === null || value === undefined ? "—" : `${value.toFixed(digits)}%`);

export type SessionMode = "pre" | "us" | "post";

/** Which part of the trading day it is, in New York time. Drives what the page leads with. */
export const sessionMode = (date: Date = new Date()): { mode: SessionMode; label: string } => {
    const ny = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const minutes = ny.getHours() * 60 + ny.getMinutes();
    const weekend = ny.getDay() === 0 || ny.getDay() === 6;
    if (weekend) return { mode: "post", label: "Markets closed" };
    if (minutes < 9 * 60 + 30) return { mode: "pre", label: minutes >= 3 * 60 ? "Europe session · US pre-market" : "Asia session" };
    if (minutes < 16 * 60) return { mode: "us", label: "US session" };
    return { mode: "post", label: "After the close" };
};

/** A tiny inline SVG sparkline path, scaled to the box. */
export const sparkPath = (values: number[], width: number, height: number, pad = 2) => {
    const clean = values.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (clean.length < 2) return "";
    const min = Math.min(...clean);
    const max = Math.max(...clean);
    const range = max - min || 1;
    return clean
        .map((v, i) => {
            const x = pad + (i * (width - 2 * pad)) / (clean.length - 1);
            const y = pad + (height - 2 * pad) * (1 - (v - min) / range);
            return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join("");
};

export type Market = { name: string; tz: string; open: number; close: number };

/** Exchange hours in local minutes after midnight (lunch breaks ignored). */
export const MARKETS: Market[] = [
    { name: "Tokyo", tz: "Asia/Tokyo", open: 9 * 60, close: 15 * 60 },
    { name: "London", tz: "Europe/London", open: 8 * 60, close: 16 * 60 + 30 },
    { name: "New York", tz: "America/New_York", open: 9 * 60 + 30, close: 16 * 60 },
];

export const marketClock = (market: Market, now: Date) => {
    const local = new Date(now.toLocaleString("en-US", { timeZone: market.tz }));
    const minutes = local.getHours() * 60 + local.getMinutes();
    const weekday = local.getDay() > 0 && local.getDay() < 6;
    return {
        time: `${String(local.getHours()).padStart(2, "0")}:${String(local.getMinutes()).padStart(2, "0")}`,
        open: weekday && minutes >= market.open && minutes < market.close,
    };
};

/**
 * Keep one locked alt-data teaser per list. Below Advanced every alt-data item arrives
 * locked; one says "there is something here, upgrade to see it", a run of them just
 * crowds out data the reader can use.
 */
export const oneLockedTeaser = <T extends { locked?: boolean }>(items: T[]): T[] => {
    let seen = false;
    return items.filter((item) => {
        if (!item.locked) return true;
        if (seen) return false;
        seen = true;
        return true;
    });
};
